import { useEffect, useState } from "react";
import { api } from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import { getAppLanguage, toLocale } from "../../utils/appLanguage";
import { isSameRegion } from "../../utils/regions";

/** One purchased region plan, as GET /payment/subscriptions returns it. */
export interface RegionSub {
  region: string;
  planName?: string | null;
  cadence?: "monthly" | "annual";
  startDate?: string;
  endDate?: string;
  status?: string;
}

export interface SubState {
  sub?: RegionSub;
  /** Paid plan that has not ended. */
  owned: boolean;
  /** Paid plan whose end date has passed (or whose status says so). */
  expired: boolean;
  /** Still running, but set to end instead of renewing. */
  cancelled: boolean;
  daysLeft: number | null;
}

const DAY_MS = 86_400_000;

export const parseDate = (value?: string): Date | null => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** "12 Oct 2026" in the app language. */
export const formatDay = (value?: string): string => {
  const d = parseDate(value);
  if (!d) return "—";
  try {
    return new Intl.DateTimeFormat(toLocale(getAppLanguage()), {
      dateStyle: "medium",
    }).format(d);
  } catch {
    return d.toISOString().slice(0, 10);
  }
};

type UserLike = {
  subscriptionStartDate?: string;
  subscriptionEndDate?: string;
  subscriptionStatus?: string;
  unlockedRegions?: string[];
  plan?: string;
} | null;

/**
 * Works out a region's plan state. If the purchased-plans list has nothing
 * for the region, falls back to the subscription fields on the user record.
 */
export const resolveSubState = (
  subs: RegionSub[],
  regionValue: string,
  user: UserLike,
): SubState => {
  const listed = subs.find((x) => isSameRegion(x.region, regionValue));
  const fromUser: RegionSub | undefined =
    user?.subscriptionEndDate &&
    ((user.unlockedRegions || []).some((v) => isSameRegion(v, regionValue)) ||
      user.plan === "pro" ||
      user.plan === "global")
      ? {
          region: regionValue,
          startDate: user.subscriptionStartDate,
          endDate: user.subscriptionEndDate,
          status: user.subscriptionStatus,
        }
      : undefined;
  // A fresh purchase lands on the user record straight away (tier plans have
  // no region, so the list never carries them); take whichever ends last so a
  // stale expired entry cannot shadow it.
  const sub: RegionSub | undefined =
    listed && fromUser
      ? (parseDate(fromUser.endDate)?.getTime() ?? 0) >
        (parseDate(listed.endDate)?.getTime() ?? 0)
        ? fromUser
        : listed
      : listed || fromUser;

  const endDate = parseDate(sub?.endDate);
  const ended =
    !!sub &&
    (String(sub.status || "").toLowerCase() === "expired" ||
      (!!endDate && endDate.getTime() < Date.now()));
  const owned = !!sub && !ended;
  return {
    sub,
    owned,
    expired: !!sub && ended,
    cancelled: owned && String(sub?.status).toLowerCase() === "cancelled",
    daysLeft: endDate
      ? Math.max(0, Math.ceil((endDate.getTime() - Date.now()) / DAY_MS))
      : null,
  };
};

/** The signed-in account's purchased region plans. */
export const useRegionSubs = (): { subs: RegionSub[]; loaded: boolean } => {
  const { user } = useAuth();
  const [subs, setSubs] = useState<RegionSub[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!user?._id) return;
    let cancelled = false;
    api
      .get("/payment/subscriptions")
      .then((res) => {
        if (!cancelled) setSubs(res.data?.regions || []);
      })
      .catch(() => {
        // Callers fall back to the subscription fields on the user record.
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [user?._id, user?.subscriptionEndDate]);

  return { subs, loaded };
};
